import { useId } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { DashboardPoint } from '@/api/generated';
import { ChartDataTable } from '@/components/ChartDataTable';
import { moneyIn } from '@/components/moneyIn';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { bucketByWeek } from './weeklyBuckets';

export interface WeeklyBarsProps {
  series: DashboardPoint[];
  /** Wholesale ÷ retail for the period — the inner bar; the rest is uplift. */
  wholesaleRatio: number;
  currency: string;
}

export function WeeklyBars({ series, wholesaleRatio, currency }: WeeklyBarsProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const locale = useLocale();
  const buckets = bucketByWeek(series);
  const money = (minor: number) => moneyIn(minor, currency, locale, t('statements.unavailable'));
  const maxRetail = buckets.reduce((max, bucket) => Math.max(max, bucket.retailMinor), 0);
  const clampedWholesale = Math.min(1, Math.max(0, wholesaleRatio));
  const maxLabel = money(maxRetail);
  const dataId = useId();
  const bars = buckets.map((bucket) => ({
    monday: bucket.monday,
    week: formatCivilDate(bucket.monday, locale, { month: 'short', day: 'numeric' }),
    retailMinor: bucket.retailMinor,
    retail: money(bucket.retailMinor),
    wholesale: money(Math.round(bucket.retailMinor * clampedWholesale)),
  }));

  return (
    <Paper sx={{ p: 2.5, minWidth: 0, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" component="h2" sx={{ flex: 1 }}>
          {t('dashboard.chart.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, color: tokens.text2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.25, bgcolor: tokens.brandSoft }} />
            <Typography variant="caption">{t('dashboard.chart.retail')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.25, bgcolor: tokens.brand }} />
            <Typography variant="caption">{t('dashboard.chart.wholesale')}</Typography>
          </Box>
        </Box>
      </Box>

      {buckets.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2, mt: 3 }}>
          {t('dashboard.chart.empty')}
        </Typography>
      ) : (
        <>
          <Box
            role="img"
            aria-label={t('dashboard.chart.summary', { count: buckets.length, max: maxLabel })}
            aria-describedby={dataId}
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1.25,
              height: 190,
              mt: 2.25,
              borderBottom: '1px solid',
              borderColor: tokens.line,
            }}
          >
            {bars.map((bar) => {
              const outerPct = maxRetail > 0 ? (bar.retailMinor / maxRetail) * 100 : 0;
              return (
                <Tooltip
                  describeChild
                  key={bar.monday}
                  title={t('dashboard.chart.tip', {
                    week: bar.week,
                    retail: bar.retail,
                    wholesale: bar.wholesale,
                  })}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        height: `${outerPct}%`,
                        bgcolor: tokens.brandSoft,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Box sx={{ height: `${clampedWholesale * 100}%`, bgcolor: tokens.brand }} />
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, mt: 0.75 }}>
            {bars.map((bar) => (
              <Typography
                key={bar.monday}
                variant="caption"
                sx={{ flex: 1, textAlign: 'center', color: tokens.text2 }}
              >
                {bar.week}
              </Typography>
            ))}
          </Box>
          <ChartDataTable
            id={dataId}
            label={t('dashboard.chart.title')}
            columns={[
              t('dashboard.chart.week'),
              t('dashboard.chart.retail'),
              t('dashboard.chart.wholesale'),
            ]}
            rows={bars.map((bar) => ({
              key: bar.monday,
              label: bar.week,
              values: [bar.retail, bar.wholesale],
            }))}
          />
          {/* One period-wide ratio drives every week's inner bar: say so (RPT-001). */}
          <Typography variant="caption" component="p" sx={{ color: tokens.text2, mt: 1.5 }}>
            {t('dashboard.chart.basis')}
          </Typography>
        </>
      )}
    </Paper>
  );
}
